import { formatPhone } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { loadAttendanceList, markAttendance } from '../../attendance/api.js';
import { useAuthStore } from '../../auth/store.js';
import { db } from '../../sync/db.js';
import { useSyncStore } from '../../sync/store.js';

/**
 * Явка на экскурсию и стажировку.
 *
 * Отмечается прямо во время экскурсии, с телефона, по цеху, где связи нет.
 * Поэтому список берётся из локальной базы, а нажатие уходит в очередь —
 * администратор видит результат сразу и не ждёт сервер.
 */

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AttendanceScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const online = useSyncStore((state) => state.online);
  const [date, setDate] = useState(todayIso);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = useLiveQuery(() => db.attendanceLists.get(date), [date]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    void loadAttendanceList(date)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить список');
        return undefined;
      })
      .finally(() => setLoading(false));
  }, [date]);

  const mark = async (appointmentId: string, candidateId: string, showed: boolean): Promise<void> => {
    if (!user) return;
    if (navigator.vibrate) navigator.vibrate(showed ? 40 : [20, 40, 20]);
    await markAttendance(date, appointmentId, candidateId, showed, user.id);
  };

  const appointments = list?.appointments ?? [];
  const total = appointments.reduce((sum, appointment) => sum + appointment.slots.length, 0);
  const marked = appointments.reduce(
    (sum, appointment) =>
      sum + appointment.slots.filter((slot) => slot.attendance !== 'PENDING').length,
    0,
  );

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Явка</h1>
        <Link to="/admin" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-touch">
        <button
          type="button"
          onClick={() => setDate(todayIso())}
          className={`min-h-touch rounded-xl px-5 text-base font-semibold ${
            date === todayIso() ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
          }`}
        >
          Сегодня
        </button>
        <button
          type="button"
          onClick={() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setDate(tomorrow.toISOString().slice(0, 10));
          }}
          className={`min-h-touch rounded-xl px-5 text-base font-semibold ${
            date !== todayIso() ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
          }`}
        >
          Завтра
        </button>
        <span className="text-base text-dim">
          отмечено {marked} из {total}
        </span>
      </div>

      {!online && (
        <p className="mb-4 rounded-xl bg-amber-500 px-4 py-3 text-base text-black">
          Сети нет. Список — последний загруженный
          {list ? `, от ${new Date(list.fetchedAt).toLocaleString('ru-RU')}` : ''}. Отметки
          сохранятся и уйдут, когда связь появится.
        </p>
      )}
      {error && online && <p className="mb-4 text-base text-amber-700">{error}</p>}

      {loading && !list && <p className="text-lg text-dim">Загружаю…</p>}

      {list && appointments.length === 0 && (
        <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
          На этот день никто не записан
        </p>
      )}

      {appointments.map((appointment) => (
        <section key={appointment.id} className="mb-6">
          <h2 className="mb-3 text-lg font-semibold">
            {appointment.kind === 'TOUR' ? 'Экскурсия' : 'Стажировка'}{' '}
            {new Date(appointment.scheduledAt).toLocaleTimeString('ru-RU', {
              hour: '2-digit',
              minute: '2-digit',
            })}
            {appointment.site ? ` · ${appointment.site}` : ''}
          </h2>

          <ul className="flex flex-col gap-touch">
            {appointment.slots.map((slot) => (
              <li key={slot.candidateId} className="rounded-xl bg-card shadow-card p-4">
                <p className="text-lg font-semibold">{slot.fullName}</p>
                <a href={`tel:${slot.phone}`} className="text-base text-sky-400">
                  {formatPhone(slot.phone)}
                </a>

                <div className="mt-4 flex gap-touch">
                  <button
                    type="button"
                    onClick={() => void mark(appointment.id, slot.candidateId, true)}
                    className={`min-h-[64px] flex-1 rounded-xl text-lg font-semibold ${
                      slot.attendance === 'SHOWED'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-muted active:bg-line'
                    }`}
                  >
                    Пришёл
                  </button>
                  <button
                    type="button"
                    onClick={() => void mark(appointment.id, slot.candidateId, false)}
                    className={`min-h-[64px] flex-1 rounded-xl text-lg font-semibold ${
                      slot.attendance === 'NO_SHOW'
                        ? 'bg-red-600 text-white'
                        : 'bg-muted active:bg-line'
                    }`}
                  >
                    Не пришёл
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
